import { soleVariableName } from '@agavi/blocks';
import { Braces, X } from 'lucide-react';
import { useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useVariableAffordance } from './use-variable-affordance';
import { VariablePickerMenu } from './variable-picker-menu';

import type { PickerHandle } from './flow-picker-menu';
import type { TaskVariable } from '@/types/experience';

import { IconButton } from '@/components/ui/icon-button';
import { NumberField } from '@/components/ui/number-field';

/**
 * A number, or ONE variable. Never arithmetic.
 *
 * The third input type, between `NumberField` and `FormulaField`, and the reason it exists rather
 * than reusing either: a DataViz cell wants to name a value, not compute one. `FormulaField` would
 * allow `100 - {dip}`, which is one expression stored in forty cells where a second variable
 * called `floor` would be one fact referenced forty times — and it would drag mathjs, angle mode,
 * cycle detection and an "unevaluable" severity into a grid that can hold four hundred cells.
 * `NumberField` alone cannot name anything.
 *
 * ## It is a two-state control, not a text field
 *
 * Because a cell is EITHER a number OR exactly one reference, there is no mixed state to edit and
 * so no need for inline chip editing — which is the entire weight of `FormulaField`. Showing a
 * number means showing `NumberField`; showing a reference means showing a chip and a way back.
 * That also makes the rule visible rather than enforced: you cannot type arithmetic here because
 * there is nowhere to type it, instead of typing it and being told no.
 *
 * ## The third case for typed braces
 *
 * `feedback_variable_chip_vs_typed_braces_rule` says typed `{}` is literal text everywhere except
 * a formula field, where it converts in front of the teacher. This is the third surface: `{` is
 * not a legal character in the number grammar, so pressing it can only mean "I want a variable" —
 * it opens the picker rather than being swallowed. Nothing is ever converted behind the teacher's
 * back; the picker is the only way a reference gets written.
 */
export function VariableField({
  value,
  onChange,
  variables,
  ariaLabel,
  fieldId,
  hasError,
  placeholder,
  onKeyDown,
  className = '',
}: {
  /** Either a number as text, or `{name}` — the stored form, exactly as it round-trips. */
  value: string;
  onChange: (next: string) => void;
  variables: TaskVariable[];
  ariaLabel: string;
  fieldId?: string;
  hasError?: boolean;
  placeholder?: string;
  /** Forwarded to the number input. Not applied to the chip state, which has no text to type in. */
  onKeyDown?: (e: ReactKeyboardEvent) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const pickerHandle = useRef<PickerHandle | null>(null);
  /*
   * DataViz is a rung-3 block: a chart whose data varies can be answered by an MCQ, and then the
   * correct OPTION has to vary with it. Grandfathered on the cell's own contents, so a cell
   * already naming a variable keeps its picker and can be cleared.
   */
  const varsAllowed = useVariableAffordance() === 'allowed';
  const inputWrapRef = useRef<HTMLSpanElement | null>(null);
  const name = soleVariableName(value);

  if (name) {
    return (
      <span className={`flex items-center gap-1 ${className}`}>
        {/* The chip is the value, so it is not a button: clicking it would suggest editing, and
            there is nothing inside it to edit. The way back is to remove it. */}
        <span className='ws-var-chip min-w-0 flex-1 truncate' data-field-id={fieldId}>{`{${name}}`}</span>
        <IconButton
          size='xs'
          aria-label={t('blocks.dataviz.grid.clearVariable', { name })}
          onClick={() => onChange('')}
        >
          <X className='h-3 w-3' />
        </IconButton>
      </span>
    );
  }

  return (
    <span ref={inputWrapRef} className={`flex items-center gap-1 ${className}`}>
      <NumberField
        grammar='scientific'
        allowNegative
        value={value}
        onChange={onChange}
        ariaLabel={ariaLabel}
        fieldId={fieldId}
        hasError={hasError}
        placeholder={placeholder}
        className='min-w-0 flex-1'
        onKeyDown={e => {
          // `{` cannot be part of a number, so the keystroke has only one possible meaning.
          /* Opened on the FIELD's box rather than the caret: a number field is narrow enough that
             the two are within a few pixels, and `getBoundingClientRect` on the wrapper is stable
             where a caret rect in an `<input>` is not readable at all. */
          if (e.key === '{' && varsAllowed) {
            e.preventDefault();
            const rect = inputWrapRef.current?.getBoundingClientRect();
            if (rect) pickerHandle.current?.openAt(rect);
            return;
          }
          onKeyDown?.(e);
        }}
      />
      {varsAllowed && (
      <VariablePickerMenu
        variables={variables}
        onChoose={chosen => onChange(`{${chosen}}`)}
        ariaLabel={t('blocks.dataviz.grid.useVariable')}
        handleRef={pickerHandle}
        renderTrigger={({ ref, ...props }) => (
          <IconButton
            ref={ref}
            size='xs'
            aria-label={t('blocks.dataviz.grid.useVariable')}
            {...props}
          >
            <Braces className='h-3 w-3' />
          </IconButton>
        )}
      />
      )}
    </span>
  );
}
